import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Models } from "api/types";
import { Button } from "atoms/button/Button";
import { Text } from "atoms/Text/Text";

import { RetailProductOrderReturnLineItemCard } from "../RetailProductOrderReturnLineItemCard/RetailProductOrderReturnLineItemCard";

interface ReturnItemSelectionProps {
  orderId: string;
  lineItemsForShipping: Models<"DisplayRetailProductOrderLineItemForShipping">[];
  selectedItem?: Models<"DisplayRetailProductOrderLineItemForShipping"> & {
    instanceId?: string;
  };
  onSelectionChange?: (
    selectedItem: Models<"DisplayRetailProductOrderLineItemForShipping"> & {
      instanceId: string;
    }
  ) => void;
  retailProductRefunds: Models<"DisplayRetailProductRefundSupportDetailsForOrder">;
}

export const ReturnItemSelection = ({
  orderId,
  lineItemsForShipping,
  selectedItem,
  onSelectionChange,
  retailProductRefunds,
}: ReturnItemSelectionProps) => {
  const navigate = useNavigate();

  const itemsWithUuids = useMemo(() => {
    return lineItemsForShipping.flatMap(
      (item: Models<"DisplayRetailProductOrderLineItemForShipping">) =>
        Array.from({ length: item.totalQuantity }, (_, index) => ({
          ...item,
          instanceId: crypto.randomUUID(), // UUID to distinguish between instances
          originalIndex: index,
        }))
    );
  }, [lineItemsForShipping]);

  const getItemRefundStatus = (item: any) => {
    if (
      !retailProductRefunds ||
      retailProductRefunds.orderId.value !== orderId
    ) {
      return false;
    }

    const refundedItems =
      retailProductRefunds.items?.filter(
        (refundItem: Models<"DisplayRetailProductRefundSupportInfoForItem">) =>
          refundItem.retailProductVariantId.value === item.packageId &&
          refundItem.retailProductListingId.value === item.packageListingId
      ) || [];

    const refundedIndices = refundedItems.map((_, index) => index);
    const isThisInstanceRefunded = refundedIndices.includes(item.originalIndex);

    return isThisInstanceRefunded;
  };

  return (
    <div className="flex min-h-screen flex-col justify-between pb-20">
      <div className="flex flex-col gap-2 p-4">
        <Text variant="bold1">Return/Refund</Text>
        <Text variant="body2">
          Which product in this order would you like help with?
        </Text>

        {itemsWithUuids.map((itemWithUuid) => {
          const isDisabled = getItemRefundStatus(itemWithUuid);
          return (
            <RetailProductOrderReturnLineItemCard
              key={itemWithUuid.instanceId}
              thumbnailImageUri={itemWithUuid.thumbnailImageUri ?? undefined}
              name={itemWithUuid.name.text}
              packageId={itemWithUuid.packageId}
              isSelected={selectedItem?.instanceId === itemWithUuid.instanceId}
              disabled={isDisabled}
              disabledMessage={isDisabled ? "Return requested" : undefined}
              onSelect={() => onSelectionChange?.(itemWithUuid)}
            />
          );
        })}
      </div>

      <div className="w-full px-4">
        <Button
          fullWidth
          style="primary"
          disabled={!selectedItem || getItemRefundStatus(selectedItem)}
          onClick={() => {
            if (selectedItem && !getItemRefundStatus(selectedItem)) {
              navigate(
                `/products/order/${orderId}/return/${selectedItem?.packageId}`,
                {
                  state: {
                    instanceId: selectedItem?.instanceId,
                  },
                }
              );
            }
          }}
        >
          Next
        </Button>
      </div>
    </div>
  );
};
